import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, Menu, Typography } from '@mui/material';
import { useState } from 'react';

import { FlagIcon, ToggleableOption } from '@/components';
import { Regions } from '@/constants';
import { appStyles } from '@/theme';
import { Region } from '@/types';

const USER_REGION_DATA = {
  [Regions.CAN]: {
    label: 'Canada',
    labelShort: 'CA',
  },
  [Regions.USA]: {
    label: 'United States',
    labelShort: 'US',
  },
  [Regions.GBR]: {
    label: 'United Kingdom',
    labelShort: 'UK',
  },
} as const;

interface RegionSelectDropdownProps {
  selectedRegion: Region;
  onSelect: (region: Region) => void;
}

export const RegionSelectDropdown = ({
  selectedRegion,
  onSelect,
}: RegionSelectDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleSelect = (region: Region) => {
    onSelect(region);
    handleClose();
  };

  return (
    <>
      <Box
        role="button"
        onClick={handleClick}
        aria-controls="regions-menu"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          p: 1,
        }}>
        <FlagIcon region={selectedRegion} />
        <Typography
          variant="body2SemiBold"
          sx={{ ml: 0.5, color: 'text.contrast' }}>
          {USER_REGION_DATA[selectedRegion].labelShort}
        </Typography>
        <KeyboardArrowDown sx={{ width: 18, height: 18 }} />
      </Box>
      <Menu
        id="regions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: 220,
              ...appStyles.popoverPaper,
            },
          },
          list: {
            sx: {
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            },
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        {Object.entries(USER_REGION_DATA).map(([region, { label }]) => (
          <ToggleableOption
            key={region}
            label={label}
            isSelected={selectedRegion === region}
            onClick={() => handleSelect(region as Region)}
            startIcon={<FlagIcon region={region} width={24} />}
          />
        ))}
      </Menu>
    </>
  );
};
